import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/Modal';

const PAY_LABEL = { cash: 'Cash', stripe: 'Stripe · Card', bank: 'Bank Transfer', viva: 'Viva Wallet', paypal: 'PayPal' };
const PAY_METHODS = [
  { id: 'cash',   label: '💵 Cash' },
  { id: 'stripe', label: '💳 Card (Stripe)' },
  { id: 'viva',   label: '🔵 Viva Wallet' },
  { id: 'bank',   label: '🏦 Bank Transfer' },
];

const fmtLabel = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

export default function ExtendRentalModal() {
  const { modals, closeModal, rentals, vehicles, setRentals, addInvoice, nextInvoiceNum, companySettings, showToast, currentUserId } = useApp();
  const open = modals['extend-rental'];
  const payload = modals['extend-rental-vid'];
  const targetVid = (payload && typeof payload === 'object') ? payload.vid : payload;
  const seedEnd   = (payload && typeof payload === 'object') ? payload.newEnd : null;

  const rental  = targetVid ? rentals.find(r => r.vid === targetVid && ['active', 'overdue'].includes(r.status)) : null;
  const vehicle = targetVid ? vehicles.find(v => v.id === targetVid) : null;

  const [newEnd, setNewEnd]               = useState('');
  const [newEndTime, setNewEndTime]       = useState('09:00'); // return time (HH:MM)
  const [payTiming, setPayTiming]         = useState('now');   // 'now' | 'return'
  const [payMethod, setPayMethod]         = useState('cash');
  const [chargeOverride, setChargeOverride] = useState('');    // '' = use proportional suggestion

  // Seed the new return date (from a calendar drag if provided, else +1 day)
  useEffect(() => {
    if (open && rental) {
      if (seedEnd) {
        setNewEnd(seedEnd);
      } else {
        const base = new Date(rental.end);
        base.setDate(base.getDate() + 1);
        const pad = n => String(n).padStart(2, '0');
        setNewEnd(`${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}`);
      }
      setNewEndTime(rental.t2 || (rental.end ? rental.end.slice(11, 16) : '09:00') || '09:00');
      setPayTiming('now');
      setPayMethod('cash');
      setChargeOverride('');
    }
  }, [open, rental?.id, seedEnd]);

  if (!open) return null;

  // ── Derived figures ────────────────────────────────────────────────────────
  const vatRate = parseFloat(companySettings?.vatRate || 19) / 100;
  const endTime = newEndTime || '09:00';
  const newEndIso = newEnd ? `${newEnd}T${endTime}` : null;

  const oldDays  = rental ? Math.max(1, Math.round((new Date(rental.end) - new Date(rental.start)) / 86400000)) : 0;
  const newDays  = (rental && newEndIso) ? Math.max(1, Math.round((new Date(newEndIso) - new Date(rental.start)) / 86400000)) : oldDays;
  const extraDays = Math.max(0, newDays - oldDays);
  // Proportional scaling preserves VAT / insurance / extras already baked into agreedPrice
  const proportionalPrice = rental ? Math.round((rental.agreedPrice || 0) * (newDays / oldDays) * 100) / 100 : 0;
  const suggestedCharge = rental ? Math.round((proportionalPrice - (rental.agreedPrice || 0)) * 100) / 100 : 0;
  // Operator can override the charge (charge more or less than the proportional suggestion)
  const overrideNum = parseFloat(chargeOverride);
  const charge = (chargeOverride !== '' && !isNaN(overrideNum) && overrideNum >= 0) ? Math.round(overrideNum * 100) / 100 : suggestedCharge;
  const newTotal = rental ? Math.round(((rental.agreedPrice || 0) + charge) * 100) / 100 : 0;
  const perDay = extraDays > 0 ? +(charge / extraDays).toFixed(2) : 0;
  // The charge is a GROSS amount (matches how agreedPrice is stored) — VAT is contained within it
  const chargeVat = +(charge - charge / (1 + vatRate)).toFixed(2);

  // Availability — any other booking/rental for this vehicle inside the extension window
  const conflict = (rental && newEndIso) ? rentals.find(r =>
    r.id !== rental.id &&
    r.vid === targetVid &&
    ['active', 'overdue', 'booked'].includes(r.status) &&
    new Date(r.start) < new Date(newEndIso) &&
    new Date(r.end) > new Date(rental.end)
  ) : null;

  const valid = rental && extraDays > 0 && !conflict && (payTiming === 'return' || !!payMethod);

  function submit() {
    if (!rental) return;
    if (extraDays <= 0) { showToast('Pick a later return date'); return; }
    if (conflict) { showToast(`Can't extend — vehicle is booked again from ${conflict.startLabel || fmtLabel(conflict.start)}`); return; }

    // Extension pro forma (separate document; original stays as-is)
    const num = nextInvoiceNum();
    const today = new Date();
    // Charge is gross (what you collect); always break VAT out so total = charge
    const amt    = +charge.toFixed(2);
    const sub    = +(amt / (1 + vatRate)).toFixed(2);
    const vatAmt = +(amt - sub).toFixed(2);
    const paidNow = payTiming === 'now';

    addInvoice({
      id: num, num,
      customer: rental.customer, email: rental.email || '', addr: rental.address || '',
      vehicle: vehicle?.name || '', contract: rental.id, contractId: rental.id, vid: rental.vid,
      method: paidNow ? payMethod : '',
      methodLabel: paidNow ? (PAY_LABEL[payMethod] || payMethod) : 'Due at return',
      issue: today.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
      due:   fmtLabel(newEndIso),
      issueDate: today.toISOString().slice(0, 10), dueDate: newEnd,
      lines: [{
        desc: `Rental extension — ${extraDays} extra day${extraDays > 1 ? 's' : ''} (${rental.endLabel || fmtLabel(rental.end)} → ${fmtLabel(newEndIso)})`,
        qty: extraDays, unit: perDay, total: amt,
      }],
      subtotal: sub, vatRate: Math.round(vatRate * 100), vatAmt,
      discount: 0, discType: 'eur', total: amt,
      notes: `Rental extension · ${rental.id}`,
      status: paidNow ? 'paid' : 'pending',
      source: 'extension',
    });

    // Update the rental: new end, new total, count. Re-activate if it was overdue and now ends in the future.
    setRentals(prev => prev.map(r => r.id === rental.id ? {
      ...r,
      end: newEndIso,
      endLabel: fmtLabel(newEndIso),
      t2: endTime,
      calSpan: newDays, days: newDays,
      agreedPrice: +newTotal.toFixed(2),
      status: new Date(newEndIso) > new Date() ? 'active' : r.status,
      extensionCount: (r.extensionCount || 0) + 1,
      extendedAt: new Date().toISOString(),
      ...(paidNow && payMethod === 'cash'
        ? { cashCollectedBy: r.cashCollectedBy || currentUserId }
        : {}),
      ...(paidNow ? {} : { extensionDueAtReturn: (parseFloat(r.extensionDueAtReturn) || 0) + amt }),
    } : r));

    closeModal('extend-rental');
    showToast(paidNow
      ? `✓ Extended to ${fmtLabel(newEndIso)} — €${amt.toFixed(2)} collected · pro forma created`
      : `✓ Extended to ${fmtLabel(newEndIso)} — €${amt.toFixed(2)} to collect at return`);
  }

  return (
    <Modal open={!!open} onClose={() => closeModal('extend-rental')}>
      <ModalHeader
        title="Extend Rental"
        subtitle={rental ? `${rental.id} · ${rental.customer}` : undefined}
        onClose={() => closeModal('extend-rental')}
      />
      <ModalBody>
        {!rental ? (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--tx3)' }}>No active rental found for this vehicle.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Current rental summary */}
            <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '10px 12px', fontSize: 11 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--tx3)' }}>Vehicle</span>
                <span style={{ fontWeight: 600 }}>{vehicle?.icon} {vehicle?.name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--tx3)' }}>Current period</span>
                <span>{rental.startLabel || fmtLabel(rental.start)} → {rental.endLabel || fmtLabel(rental.end)} · {oldDays}d</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--tx3)' }}>Current total</span>
                <span style={{ fontWeight: 700, color: 'var(--gr)' }}>€{(rental.agreedPrice || 0).toFixed(2)}</span>
              </div>
            </div>

            {/* New return date + time */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 10 }}>
              <div className="fg">
                <label className="fl">New return date</label>
                <input type="date" className="fi" value={newEnd} min={rental.end ? rental.end.slice(0, 10) : undefined}
                  onChange={e => setNewEnd(e.target.value)} />
              </div>
              <div className="fg">
                <label className="fl">Return time</label>
                <input type="time" className="fi" value={newEndTime}
                  onChange={e => setNewEndTime(e.target.value)} />
              </div>
            </div>

            {/* Conflict warning */}
            {conflict && (
              <div style={{ background: 'var(--ac-l)', border: '1px solid var(--ac-b)', borderRadius: 'var(--r)', padding: '8px 12px', fontSize: 11, color: 'var(--ac)', fontWeight: 600 }}>
                ⚠ This vehicle is booked again from {conflict.startLabel || fmtLabel(conflict.start)} ({conflict.customer || 'another booking'}). Choose an earlier date or reassign.
              </div>
            )}

            {/* Extension cost breakdown */}
            {extraDays > 0 && !conflict && (
              <div style={{ background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 'var(--r)', padding: '10px 12px', fontSize: 11 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ color: 'var(--tx3)' }}>Extra days</span>
                  <span style={{ fontWeight: 600 }}>+{extraDays} day{extraDays > 1 ? 's' : ''} · ≈ €{perDay.toFixed(2)}/day</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ color: 'var(--tx3)' }}>New period</span>
                  <span>{rental.startLabel || fmtLabel(rental.start)} → {fmtLabel(newEndIso)} · {endTime} · {newDays}d</span>
                </div>
                {/* Editable charge — gross amount you collect for the extension (VAT included) */}
                <div style={{ paddingTop: 6, borderTop: '1px solid var(--bl-b)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: 'var(--tx3)' }}>Extension charge <span style={{ fontSize: 9 }}>(incl. VAT)</span></span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ color: 'var(--tx3)' }}>€</span>
                      <input type="number" min="0" step="0.01"
                        value={chargeOverride}
                        placeholder={suggestedCharge.toFixed(2)}
                        onChange={e => setChargeOverride(e.target.value)}
                        style={{ width: 90, textAlign: 'right', fontFamily: "'DM Mono',monospace", fontWeight: 700, color: 'var(--gr)', padding: '4px 6px', border: '1px solid var(--bd)', borderRadius: 6, background: 'var(--sur)' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: 'var(--tx3)' }}>
                    <span>
                      Suggested €{suggestedCharge.toFixed(2)} (proportional)
                      {chargeOverride !== '' && Math.abs(charge - suggestedCharge) > 0.001 && (
                        <button type="button" onClick={() => setChargeOverride('')}
                          style={{ marginLeft: 6, border: 'none', background: 'transparent', color: 'var(--bl)', cursor: 'pointer', fontSize: 10, textDecoration: 'underline', padding: 0 }}>reset</button>
                      )}
                    </span>
                    <span>incl. VAT €{chargeVat.toFixed(2)}</span>
                  </div>
                </div>

                {/* What gets collected now vs the rental's new grand total */}
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--bl-b)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                    <span style={{ color: 'var(--tx2)' }}>{payTiming === 'now' ? 'Collect now' : 'Collect at return'}</span>
                    <span style={{ color: 'var(--gr)', fontFamily: "'DM Mono',monospace" }}>€{charge.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2, fontSize: 10, color: 'var(--tx3)' }}>
                    <span>New rental total (was €{(rental.agreedPrice || 0).toFixed(2)})</span>
                    <span>€{newTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Payment timing */}
            {extraDays > 0 && !conflict && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Payment for the extension</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {[
                    ['now',    '💳 Charge now', 'Collect the difference immediately — pro forma marked paid.'],
                    ['return', '🕒 Collect at return', 'Pro forma stays pending and is collected when the vehicle comes back (due at desk).'],
                  ].map(([val, label, hint]) => (
                    <div key={val} onClick={() => setPayTiming(val)} style={{
                      padding: '8px 12px', border: `1.5px solid ${payTiming === val ? 'var(--ac)' : 'var(--bd)'}`,
                      borderRadius: 'var(--r)', cursor: 'pointer',
                      background: payTiming === val ? 'var(--ac-l)' : 'var(--sur)',
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 1 }}>{hint}</div>
                    </div>
                  ))}
                </div>

                {payTiming === 'now' && (
                  <div style={{ marginTop: 8 }}>
                    <label className="fl">Method</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 6, marginTop: 4 }}>
                      {PAY_METHODS.map(m => (
                        <div key={m.id} onClick={() => setPayMethod(m.id)} style={{
                          padding: '7px 10px', border: `1.5px solid ${payMethod === m.id ? 'var(--ac)' : 'var(--bd)'}`,
                          borderRadius: 'var(--r)', cursor: 'pointer', fontSize: 11, textAlign: 'center',
                          background: payMethod === m.id ? 'var(--ac-l)' : 'var(--sur)',
                        }}>{m.label}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost" onClick={() => closeModal('extend-rental')}>Cancel</button>
        <button className="btn btn-primary" style={{ background: 'var(--gr)', borderColor: 'var(--gr)' }} onClick={submit} disabled={!valid}>
          {payTiming === 'now' ? `▶ Extend & collect €${charge > 0 ? charge.toFixed(2) : '0.00'}` : `▶ Extend · €${charge > 0 ? charge.toFixed(2) : '0.00'} at return`}
        </button>
      </ModalFooter>
    </Modal>
  );
}
